import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useEditMode } from '@/contexts/EditModeContext';
import { useProduct } from '@/hooks/useProducts';
import { useProductEditor } from '@/hooks/useProductEditor';
import { useProductQA } from '@/hooks/useProductQA';
import { useProductPlans } from '@/hooks/useProductPlans';
import { useBookingModal } from '@/hooks/useBookingModal';
import { useProductViewTracking } from '@/hooks/useBrowsingActivity';
import { useProductPromoCodes } from '@/hooks/usePromoCodes';
import { mockProducts } from '@/lib/mockData';
import { requireUser } from '@/lib/requireUser';
import { trackGateEvent } from '@/data/gateAnalytics';
import { isFeatureEnabled } from '@/lib/featureFlags';

export function useProductDetailState() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isEditor, membership } = useAuth();
  const { isEditMode } = useEditMode();
  
  // Data fetching
  const { data: dbProduct, isLoading } = useProduct(slug || '');
  const product = dbProduct || mockProducts.find(p => p.id === slug);
  
  // Track product view
  useProductViewTracking(product?.id);
  
  // Product editing
  const { updateProduct, deleteProduct, isDeleting } = useProductEditor();
  const { updateQA, generateQA, isGenerating } = useProductQA();
  const { updatePlans } = useProductPlans();
  const { openBooking, BookingModal } = useBookingModal(product?.title || '', product?.booking_url);
  
  // Promo codes
  const { data: promoCodes } = useProductPromoCodes(product?.id || '');
  const hasPromo = isFeatureEnabled('promoCodes') && promoCodes && promoCodes.length > 0;
  
  // UI State
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(product?.image || '');
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [tacoFilter, setTacoFilter] = useState('all');
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('reviews');
  const [enrichModalOpen, setEnrichModalOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showGate, setShowGate] = useState(false);
  const [gateShownAt, setGateShownAt] = useState<number | null>(null);
  
  // Product type checks
  const isService = !!product?.service_type;
  const isCourse = product?.service_type === 'education';
  const hasAffiliatePlans = product?.plans?.some(plan => plan.features?.affiliate_url);
  
  // Scroll handling for header visibility
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setHeaderVisible(currentScrollY < 50);
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);
  
  // Product gate overlay logic - only for guest users
  useEffect(() => {
    if (user) return;
    
    let timer: number;
    let triggered = false;
    
    // Timer: 6 seconds
    timer = window.setTimeout(() => {
      if (!triggered) {
        triggered = true;
        const now = Date.now();
        setGateShownAt(now);
        trackGateEvent('impression', slug, 'timer');
        setShowGate(true);
      }
    }, 6000);
    
    // Scroll: 30% of page
    const onScroll = () => {
      const scrollPercent = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent > 30 && !triggered) {
        triggered = true;
        const now = Date.now();
        setGateShownAt(now);
        trackGateEvent('impression', slug, 'scroll');
        setShowGate(true);
      }
    };
    
    window.addEventListener('scroll', onScroll);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, [user, slug]);
  
  // Sync selectedImage with product.image
  useEffect(() => {
    if (product?.image) {
      setSelectedImage(product.image);
    }
  }, [product?.image]);
  
  // Handlers
  const handleUpdateField = async (field: string, value: any) => {
    if (slug) {
      await updateProduct({ productId: slug, field, value });
    }
  };
  
  const handleUpdateQA = async (qaItems: { question: string; answer: string }[]) => {
    if (slug) {
      await updateQA({ productId: slug, qaItems });
    }
  };
  
  const handleSavePlans = async (plans: Array<{ name: string; price: number; features: string[] }>) => {
    if (slug) {
      await updatePlans({ productId: slug, plans });
    }
  };
  
  const handleBuyNow = () => {
    if (hasAffiliatePlans) {
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (isCourse && product?.access_url) {
      window.open(product.access_url, '_blank');
      return;
    }
    if (isService && product?.booking_url) {
      openBooking('Discovery Call');
    } else {
      requireUser({
        user,
        navigate,
        currentPath: location.pathname,
        action: () => setPurchaseModalOpen(true)
      });
    }
  };
  
  const handleSelectPlan = (planIndex: number) => {
    if (hasAffiliatePlans) return;
    if (isService && product?.booking_url && product?.plans[planIndex]) {
      openBooking(product.plans[planIndex].name);
    } else {
      handleBuyNow();
    }
  };
  
  const handleDelete = () => {
    deleteProduct(slug!);
    setShowDeleteDialog(false);
    navigate('/browse');
  };
  
  return {
    // Route params
    slug,
    
    // Auth & editing
    user,
    isEditor,
    membership,
    isEditMode,
    
    // Data
    product,
    isLoading,
    promoCodes,
    hasPromo,
    
    // Product types
    isService,
    isCourse,
    hasAffiliatePlans,
    
    // UI State
    purchaseModalOpen,
    setPurchaseModalOpen,
    selectedImage,
    setSelectedImage,
    demoModalOpen,
    setDemoModalOpen,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    tacoFilter,
    setTacoFilter,
    isDescriptionExpanded,
    setIsDescriptionExpanded,
    activeTab,
    setActiveTab,
    enrichModalOpen,
    setEnrichModalOpen,
    showDeleteDialog,
    setShowDeleteDialog,
    headerVisible,
    showGate,
    setShowGate,
    gateShownAt,
    
    // Handlers
    handleUpdateField,
    handleUpdateQA,
    handleSavePlans,
    handleBuyNow,
    handleSelectPlan,
    handleDelete,
    
    // Mutations
    isDeleting,
    isGenerating,
    generateQA,
    
    // Booking
    openBooking,
    BookingModal,
    
    // Navigation
    navigate,
  };
}
